import React, {
    ChangeEventHandler,
    CSSProperties,
    FocusEventHandler,
    forwardRef,
    KeyboardEventHandler,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AreaContext } from '../area-provider/AreaContextProvider';
import { StyledInputRightElement } from '../input/Input.styles';
import {
    StyledTextArea,
    StyledTextAreaContent,
    StyledTextAreaContentWrapper,
    StyledTextAreaInput,
    StyledTextAreaLabel,
    StyledTextAreaLabelWrapper,
} from './TextArea.styles';
import { useCursorRepaint } from '../../hooks/resize';

type TextAreaColors = {
    backgroundColor: CSSProperties['backgroundColor'];
    borderColor: CSSProperties['borderColor'];
};

export type TextAreaProps = {
    /**
     * Disables the text area so that it cannot be changed.
     */
    isDisabled?: boolean;
    /**
     * If true, the text area is marked as invalid
     */
    isInvalid?: boolean;
    /**
     * The maximum height of the text area.
     */
    maxHeight?: CSSProperties['maxHeight'];
    /**
     * The minimum height of the text area.
     */
    minHeight?: CSSProperties['minHeight'];
    /**
     * Function that is executed when the text area loses focus.
     */
    onBlur?: FocusEventHandler<HTMLTextAreaElement>;
    /**
     * Function that is executed when the text of the text area changes.
     */
    onChange?: ChangeEventHandler<HTMLTextAreaElement>;
    /**
     * Function that is executed when the input field is focused
     */
    onFocus?: FocusEventHandler<HTMLTextAreaElement>;
    /**
     * Function that is executed when a letter is pressed
     */
    onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
    /**
     * Placeholder for the text area field.
     */
    placeholder?: string | ReactElement;
    /**
     * An element that should be displayed on the right side of the Input.
     */
    rightElement?: ReactElement;
    /**
     * Value if the text area should be controlled.
     */
    value?: string;
    /**
     * Provide custom colors to the TextArea Component
     */
    colors?: TextAreaColors;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            isDisabled,
            isInvalid,
            placeholder,
            value,
            onChange,
            onFocus,
            onKeyDown,
            rightElement,
            onBlur,
            maxHeight = '120px',
            minHeight = '41px',
            colors,
        },
        ref,
    ) => {
        'use memo';

        const [isOverflowing, setIsOverflowing] = useState(false);

        const areaProvider = useContext(AreaContext);

        const textareaRef = useRef<HTMLTextAreaElement>(null);

        useCursorRepaint(textareaRef);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const shouldShowBorder = rightElement?.props?.style?.backgroundColor === undefined;

        const shouldChangeColor = useMemo(
            () => areaProvider.shouldChangeColor ?? false,
            [areaProvider.shouldChangeColor],
        );

        const adjustTextareaHeight = useCallback(() => {
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

                setIsOverflowing(
                    textareaRef.current.scrollHeight > parseInt(maxHeight.toString(), 10),
                );
            }
        }, [maxHeight]);

        useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

        /**
         * This hook calculates the height of the TextArea after the displayValue is changed and the content is inside the "textareaRef".
         * To maintain the functionality while clearing the input, the length need to be greater than -1.
         */
        useEffect(() => {
            if (typeof value === 'string' && value.length > -1) {
                adjustTextareaHeight();
            }
        }, [adjustTextareaHeight, value]);

        const hasValue = value !== undefined && value.length > 0;

        const labelPosition = useMemo(
            () =>
                hasValue
                    ? {
                          bottom: '2px',
                          right: '2px',
                      }
                    : {
                          top: '12px',
                          left: '10px',
                      },
            [hasValue],
        );

        return useMemo(
            () => (
                <StyledTextArea $isDisabled={isDisabled}>
                    <StyledTextAreaContentWrapper
                        $isInvalid={isInvalid}
                        $shouldChangeColor={shouldChangeColor}
                        $backgroundColor={colors?.backgroundColor}
                        $borderColor={colors?.borderColor}
                    >
                        <StyledTextAreaContent>
                            <StyledTextAreaInput
                                className="chayns-scrollbar"
                                disabled={isDisabled}
                                $isInvalid={isInvalid}
                                ref={textareaRef}
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                                onFocus={onFocus}
                                onKeyDown={onKeyDown}
                                $maxHeight={maxHeight}
                                $minHeight={minHeight}
                                $isOverflowing={isOverflowing}
                                rows={1}
                            />
                            <StyledTextAreaLabelWrapper
                                animate={{ fontSize: hasValue ? '9px' : undefined }}
                                initial={false}
                                style={labelPosition}
                                transition={{ type: 'tween', duration: 0.1 }}
                            >
                                <StyledTextAreaLabel $isInvalid={isInvalid}>
                                    {placeholder}
                                </StyledTextAreaLabel>
                            </StyledTextAreaLabelWrapper>
                        </StyledTextAreaContent>
                        {rightElement && shouldShowBorder && rightElement}
                    </StyledTextAreaContentWrapper>
                    {rightElement && !shouldShowBorder && (
                        <StyledInputRightElement>{rightElement}</StyledInputRightElement>
                    )}
                </StyledTextArea>
            ),
            [
                isDisabled,
                isInvalid,
                shouldChangeColor,
                colors?.backgroundColor,
                colors?.borderColor,
                value,
                onBlur,
                onChange,
                onFocus,
                onKeyDown,
                maxHeight,
                minHeight,
                isOverflowing,
                hasValue,
                labelPosition,
                placeholder,
                rightElement,
                shouldShowBorder,
            ],
        );
    },
);

TextArea.displayName = 'TextArea';

export default TextArea;
